import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { AtSign, Loader2 } from 'lucide-react'
import { useTranslation } from '@/hooks/useTranslation'
import { useProgramContext } from '@/context/ProgramContext'
import { useTeamContext } from '@/context/TeamContext'
import { canManageProjectSettings } from '@/lib/programPermissions'
import { getApiBaseUrl } from '@/lib/api'
import { getAuthHeaders } from '@/services/api'
import {
	fetchBrandMentions,
	searchBrandMentions,
} from '@/hooks/useBrandMentions'
import { useQueryClient } from '@tanstack/react-query'
import { PlatformIcon } from '@/components/brand-mentions/PlatformIcon'
import { MentionList } from '@/components/brand-mentions/MentionList'
import { MentionDetail } from '@/components/brand-mentions/MentionDetail'
import { BrandMentionsUpgradePreview } from '@/components/brand-mentions/BrandMentionsUpgradePreview'
import { BrandMentionsScanningOverlay } from '@/components/brand-mentions/BrandMentionsScanningOverlay'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import {
	BRAND_MENTION_PLATFORMS,
	type BrandMentionPlatform,
	type BrandMentionsState,
} from '@/types/brandMentions'

function formatNextSearchAt(iso: string, locale: string) {
	return new Date(iso).toLocaleString(locale === 'fr' ? 'fr-FR' : 'en-GB', {
		timeZone: 'Europe/Paris',
		weekday: 'long',
		day: 'numeric',
		month: 'long',
		hour: '2-digit',
		minute: '2-digit',
	})
}

export default function BrandMentions() {
	const { t, i18n } = useTranslation()
	const queryClient = useQueryClient()
	const { teamId } = useTeamContext()
	const {
		programId,
		currentProgram,
		fetchPrograms,
		currentProgramAccessLevel,
	} = useProgramContext()
	const isMobile = useIsMobile()
	const [searchParams, setSearchParams] = useSearchParams()
	const [state, setState] = useState<BrandMentionsState | null>(null)
	const [error, setError] = useState('')
	const [loading, setLoading] = useState(true)
	const [searching, setSearching] = useState(false)
	const [savingEnabled, setSavingEnabled] = useState(false)
	const [platforms, setPlatforms] = useState<BrandMentionPlatform[]>([])
	const [selectedId, setSelectedId] = useState<string | null>(
		searchParams.get('mention')
	)
	const stopped = useRef(false)
	const busy = useRef(false)
	const canEditWebsite = canManageProjectSettings(currentProgramAccessLevel)
	const hasProgramWebsite = Boolean(
		currentProgram?.website_url?.trim() ||
			currentProgram?.public_reviews_website_url?.trim()
	)

	useEffect(() => {
		const mentionFromUrl = searchParams.get('mention')
		if (mentionFromUrl) setSelectedId(mentionFromUrl)
	}, [searchParams])

	useEffect(() => {
		if (!programId) {
			setLoading(false)
			return
		}
		let active = true
		setLoading(true)
		setState(null)
		setSearching(false)
		busy.current = false
		setError('')
		fetchBrandMentions(programId)
			.then((data) => {
				if (!active) return
				setState(data)
				if (data.shouldScan && data.enabled !== false) {
					setSearching(true)
				}
			})
			.catch((err) => {
				if (active) setError(err.message)
			})
			.finally(() => {
				if (active) setLoading(false)
			})
		return () => {
			active = false
			stopped.current = true
		}
	}, [programId])

	useEffect(() => {
		if (!programId || !state) return
		if (state.enabled === false || state.upgradeRequired || state.addonRequired)
			return
		if (state.needsWebsiteUrl && !hasProgramWebsite) return
		if (!state.shouldScan) return
		void search(state)
	}, [programId, hasProgramWebsite, state?.enabled, state?.shouldScan])

	async function updateEnabled(enabled: boolean) {
		if (!programId || !currentProgram || !teamId) return
		if (enabled === false) {
			stopped.current = true
			setSearching(false)
		}
		setSavingEnabled(true)
		setError('')
		try {
			const headers = await getAuthHeaders()
			const response = await fetch(
				`${getApiBaseUrl()}/api/programs/${currentProgram.id}`,
				{
					method: 'PUT',
					headers,
					body: JSON.stringify({ brand_mentions_enabled: enabled }),
				}
			)
			const data = await response.json().catch(() => ({}))
			if (!response.ok) {
				throw new Error(data.error || t('brandMentions.enableFailed'))
			}
			await fetchPrograms(teamId)
			const next = await fetchBrandMentions(programId)
			setState(next)
			await queryClient.invalidateQueries({ queryKey: ['brandMentions'] })
			if (enabled && next.shouldScan) {
				setSearching(true)
			} else {
				setSearching(false)
			}
		} catch (err) {
			setSearching(false)
			setError(
				err instanceof Error ? err.message : t('brandMentions.enableFailed')
			)
		} finally {
			setSavingEnabled(false)
		}
	}

	async function search(source?: BrandMentionsState | null) {
		const current = source ?? state
		if (
			busy.current ||
			!programId ||
			!current?.run ||
			current.enabled === false ||
			!current.shouldScan
		) {
			return
		}
		busy.current = true
		stopped.current = false
		setSearching(true)
		setError('')
		try {
			let runId = current.run.end
			let action: 'start' | 'continue' =
				current.run.pending && current.run.startedAt ? 'continue' : 'start'
			while (!stopped.current && runId) {
				const data = await searchBrandMentions(programId, action, runId)
				setState(data)
				await queryClient.invalidateQueries({ queryKey: ['brandMentions'] })
				if (!data.run?.pending) break
				action = 'continue'
				runId = data.run.end
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : t('brandMentions.searchFailed'))
		} finally {
			busy.current = false
			setSearching(false)
		}
	}

	const visible = useMemo(() => {
		const mentions = state?.mentions ?? []
		return mentions.filter((mention) => {
			if (platforms.length && !platforms.includes(mention.platform)) return false
			return true
		})
	}, [state?.mentions, platforms])

	useEffect(() => {
		if (loading) return
		if (selectedId && visible.some((mention) => mention.id === selectedId)) return
		if (isMobile) {
			if (selectedId) {
				setSelectedId(null)
				setSearchParams({}, { replace: true })
			}
			return
		}
		if (!visible.length) return
		const nextId = visible[0].id
		setSelectedId(nextId)
		setSearchParams({ mention: nextId }, { replace: true })
	}, [visible, selectedId, setSearchParams, loading, isMobile])

	const availablePlatforms = state?.platforms ?? BRAND_MENTION_PLATFORMS
	const locale = i18n.language?.startsWith('fr') ? 'fr' : 'en'
	const selectedMention = visible.find((mention) => mention.id === selectedId) ?? null
	const nextSearchMessage =
		!searching &&
		state?.enabled !== false &&
		!state?.shouldScan &&
		state?.lastSearchedAt &&
		state.nextSearchAt
			? t('brandMentions.nextSearchAt', {
					datetime: formatNextSearchAt(state.nextSearchAt, locale),
				})
			: undefined

	function handleSelect(id: string) {
		setSelectedId(id)
		setSearchParams({ mention: id })
	}

	function handleBack() {
		setSelectedId(null)
		setSearchParams({})
	}

	if (!programId) {
		return (
			<div className="flex h-full items-center justify-center p-8 text-sm text-muted-foreground">
				{t('brandMentions.selectProject')}
			</div>
		)
	}

	if (loading && !state) {
		return (
			<div className="flex h-full items-center justify-center p-8">
				<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
			</div>
		)
	}

	if (state?.upgradeRequired) {
		return <BrandMentionsUpgradePreview mode="plan" />
	}

	if (state?.addonRequired) {
		return <BrandMentionsUpgradePreview mode="addon" />
	}

	if (state && state.enabled === false) {
		const previewPlatforms = state.platforms?.length
			? state.platforms
			: BRAND_MENTION_PLATFORMS
		return (
			<div className="h-full overflow-auto p-4 sm:p-6 md:p-8">
				<div className="mx-auto w-full max-w-2xl space-y-4">
					<div className="flex items-center gap-2 text-sm font-medium text-primary">
						<AtSign className="h-4 w-4" />
						{t('brandMentions.kicker')}
					</div>
					<h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
						{t('brandMentions.title')}
					</h1>
					{error && (
						<p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
							{error}
						</p>
					)}
					<Card>
						<CardHeader>
							<CardTitle className="text-lg">
								{t('brandMentions.disabledTitle')}
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<p className="text-sm text-muted-foreground">
								{t('brandMentions.disabledDescription')}
							</p>
							<div className="flex items-center gap-1.5">
								{previewPlatforms.map((platform) => (
									<PlatformIcon
										key={platform}
										platform={platform}
										className="h-4 w-4"
										colored
									/>
								))}
							</div>
							{canEditWebsite ? (
								<div className="flex items-center justify-between gap-4 rounded-lg border p-4">
									<Label htmlFor="brand-mentions-enabled" className="cursor-pointer font-medium">
										{t('brandMentions.enableLabel')}
									</Label>
									<Switch
										id="brand-mentions-enabled"
										checked={savingEnabled}
										disabled={savingEnabled}
										onCheckedChange={(checked) => {
											if (checked) void updateEnabled(true)
										}}
									/>
								</div>
							) : (
								<p className="text-sm text-muted-foreground">
									{t('brandMentions.askAdminToEnable')}
								</p>
							)}
						</CardContent>
					</Card>
				</div>
			</div>
		)
	}

	if (state?.needsWebsiteUrl && !hasProgramWebsite) {
		return (
			<div className="h-full overflow-auto p-4 sm:p-6 md:p-8">
				<div className="mx-auto w-full max-w-2xl space-y-4">
					<div className="flex items-center gap-2 text-sm font-medium text-primary">
						<AtSign className="h-4 w-4" />
						{t('brandMentions.kicker')}
					</div>
					<h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
						{t('brandMentions.title')}
					</h1>
					{error && (
						<p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
							{error}
						</p>
					)}
					<Card>
						<CardHeader>
							<CardTitle className="text-lg">
								{t('brandMentions.websiteMissingTitle')}
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<p className="text-sm text-muted-foreground">
								{t('brandMentions.websiteMissingDescription')}
							</p>
							{canEditWebsite ? (
								<Button asChild>
									<Link to="/program-settings">
										{t('brandMentions.addWebsite')}
									</Link>
								</Button>
							) : (
								<p className="text-sm text-muted-foreground">
									{t('brandMentions.askAdminForWebsite')}
								</p>
							)}
						</CardContent>
					</Card>
				</div>
			</div>
		)
	}

	const emptyMessage = loading
		? t('brandMentions.loading')
		: state?.run?.calls?.length
			? t('brandMentions.emptyFiltered')
			: t('brandMentions.emptyStart')

	return (
		<div className="flex h-full relative overflow-hidden">
			{searching && (
				<BrandMentionsScanningOverlay
					canToggleEnabled={canEditWebsite}
					togglingEnabled={savingEnabled}
					onEnabledChange={(checked) => void updateEnabled(checked)}
				/>
			)}
			<aside
				className={cn(
					'flex-shrink-0',
					'md:relative md:w-[340px] md:min-w-[340px] md:max-w-[340px] lg:w-[380px] lg:min-w-[380px] lg:max-w-[380px] md:translate-x-0',
					'absolute inset-0 z-20 w-full md:z-auto',
					'transition-transform duration-300 ease-in-out md:transition-none',
					isMobile && selectedId ? '-translate-x-full' : 'translate-x-0'
				)}>
				<div className="flex h-full flex-col">
					{error && (
						<p role="alert" className="border-b border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
							{error}
						</p>
					)}
					{!state?.configured && state && (
						<p className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
							{t('brandMentions.notConfigured')}
						</p>
					)}
					{state?.run?.jobs
						.filter((job) => job.error)
						.map((job) => (
							<p
								key={job.id}
								className="inline-flex items-center gap-1.5 border-b px-4 py-2 text-sm text-amber-800 dark:text-amber-200">
								<PlatformIcon platform={job.platform} className="h-4 w-4" colored />
								{job.error}
							</p>
						))}
					<div className="min-h-0 flex-1">
						<MentionList
							mentions={visible}
							loading={loading}
							selectedId={selectedId}
							onSelect={handleSelect}
							availablePlatforms={[...availablePlatforms]}
							platforms={platforms}
							onTogglePlatform={(platform) =>
								setPlatforms((current) =>
									current.includes(platform)
										? current.filter((item) => item !== platform)
										: [...current, platform]
								)
							}
							onResetFilters={() => setPlatforms([])}
							canToggleEnabled={canEditWebsite}
							togglingEnabled={savingEnabled}
							onEnabledChange={(checked) => void updateEnabled(checked)}
							jobs={state?.run?.jobs ?? []}
							emptyMessage={emptyMessage}
							nextSearchMessage={nextSearchMessage}
						/>
					</div>
				</div>
			</aside>
			<main
				className={cn(
					'flex flex-1 flex-col overflow-hidden',
					'absolute inset-0 md:relative',
					'transition-transform duration-300 ease-in-out md:transition-none',
					isMobile && !selectedId ? 'translate-x-full' : 'translate-x-0'
				)}>
				<MentionDetail
					mention={selectedMention}
					brandName={state?.brandName || null}
					locale={locale}
					onBack={isMobile ? handleBack : undefined}
				/>
			</main>
		</div>
	)
}
